import { Badge, Banner, Button } from '@cloudflare/kumo';
import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAppToast } from '../hooks/useAppToast';
import { DataTable as Table } from '../components/ui/DataTable';
import { ICONS } from '../components/ui/icons';
import { SearchField } from '../components/ui/SearchField';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { InviteMemberModal, InviteResult } from '../components/InviteMemberModal';
import { useUsers } from '../hooks/useUsers';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { UserList } from '../components/UserList';
import { UserForm } from '../components/UserForm';
import { Pagination } from '../components/Pagination';
import { User, UserInput } from '../types';

export function UsersPage() {
  const toast = useAppToast();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const pageSize = 20;

  const authenticatedFetch = useAuthenticatedFetch();
  const { can, selectedTenantId, selectedTenant, organizations } = useTenant();
  const orgName = organizations.find((o) => o.id === selectedTenantId)?.name || selectedTenant?.name;

  // Convites pendentes
  type Invitation = { id: string; email: string; role: string; roleLabel: string; status: string; invitedBy: { nome: string } | null; expiresAt: string };
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<Invitation | null>(null);
  const loadInvitations = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/invitations');
      if (!res.ok) return;
      const data = await res.json();
      setInvitations(data.invitations || []);
    } catch { /* sem convites */ }
  }, [authenticatedFetch]);
  useEffect(() => { loadInvitations(); }, [loadInvitations, selectedTenantId]);

  const showInviteResult = (data: { invitation: { email: string }; link: string; emailSent: boolean; emailError?: string }) => {
    setInviteResult({ email: data.invitation.email, link: data.link, emailSent: data.emailSent, emailError: data.emailError });
    setInviteOpen(true);
    if (data.emailSent) toast.success(`Convite enviado para ${data.invitation.email}`);
    loadInvitations();
  };

  const handleInvite = async (email: string, role: string) => {
    try {
      const res = await authenticatedFetch('/api/invitations', { method: 'POST', body: JSON.stringify({ email, role }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao convidar');
      showInviteResult(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao convidar');
    }
  };

  const handleResend = async (inv: Invitation) => {
    try {
      const res = await authenticatedFetch(`/api/invitations/${inv.id}/resend`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao reenviar');
      showInviteResult(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao reenviar');
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      const res = await authenticatedFetch(`/api/invitations/${pendingRevoke.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao cancelar o convite');
      toast.success('Convite cancelado');
      loadInvitations();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao cancelar o convite');
    } finally {
      setPendingRevoke(null);
    }
  };

  // Papéis na organização ativa (RBAC)
  const [memberRoles, setMemberRoles] = useState<Record<string, string>>({});
  const loadMembers = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/user-tenants/members');
      if (!res.ok) return;
      const data = await res.json();
      setMemberRoles(Object.fromEntries((data.members || []).map((m: { id: string; role: string }) => [m.id, m.role])));
    } catch { /* sem papéis: a lista mostra o papel antigo */ }
  }, [authenticatedFetch]);
  useEffect(() => { loadMembers(); }, [loadMembers, selectedTenantId]);

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      const res = await authenticatedFetch(`/api/user-tenants/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao alterar o papel');
      toast.success(`Papel alterado para ${data.roleLabel}`);
      setMemberRoles((prev) => ({ ...prev, [userId]: role }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao alterar o papel');
      loadMembers();
    }
  };

  const { users, totalPages, loading, error, refresh } = useUsers({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  const handleCreateUser = async (data: UserInput) => {
    try {
      const response = await authenticatedFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao criar usuário');
      }
      toast.success('Usuário criado com sucesso!');
      setIsFormOpen(false);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar usuário');
    }
  };

  const handleUpdateUser = async (data: UserInput) => {
    if (!editingUser) return;
    try {
      const response = await authenticatedFetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Erro ao atualizar usuário');
      toast.success('Usuário atualizado com sucesso!');
      setIsFormOpen(false);
      setEditingUser(undefined);
      refresh();
    } catch (err) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = (id: string) => {
    setPendingDeleteId(id);
  };

  // Remove o vínculo com a organização ativa (a conta continua nas outras)
  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      const res = await authenticatedFetch(`/api/user-tenants/members/${pendingDeleteId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao remover membro');
      toast.success('Membro removido da organização');
      refresh();
      loadMembers();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao remover membro');
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleEditUser = (user: User) => { setEditingUser(user); setIsFormOpen(true); };
  const handleCloseForm = () => { setIsFormOpen(false); setEditingUser(undefined); };

  if (isFormOpen) {
    return (
      <UserForm
        user={editingUser}
        onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
        onCancel={handleCloseForm}
      />
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Membros</h2>
          <div style={{ color: 'var(--text-muted)' }}>{`${users.length} ${users.length === 1 ? 'membro' : 'membros'} nesta organização`}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {can('members:manage') && (
            <>
              <Button variant="secondary" icon={ICONS.Person} onClick={() => setIsFormOpen(true)}>
                Criar usuário
              </Button>
              <Button variant="primary" icon={ICONS.InviteUser} onClick={() => { setInviteResult(null); setInviteOpen(true); }}>
                Convidar membro
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="page-content space-y-4">
        <div className="max-w-md">
          <SearchField
            ariaLabel="Buscar usuários"
            placeholder="Buscar usuários..."
            value={search}
            onChange={(value) => { setSearch(value); setCurrentPage(1); }}
          />
        </div>

        {error && (
          <Banner variant="error" description={<>{error}</>} />
        )}

        <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
          <UserList
            users={users}
            loading={loading}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            memberRoles={memberRoles}
            canManageRoles={can('members:manage')}
            canManageOwners={can('owners:manage')}
            onRoleChange={handleRoleChange}
          />
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}

        {invitations.length > 0 && (
          <section style={{ marginTop: 32 }}>
            <h4 className="text-base font-semibold" style={{ marginBottom: 12 }}>Convites pendentes</h4>
            <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
              <Table
                variant="borderless"
                items={invitations}
                columnDefinitions={[
                  {
                    id: 'email',
                    header: 'E-mail',
                    cell: (inv: Invitation) => <div className="font-medium !text-[var(--text)]">{inv.email}</div>,
                  },
                  {
                    id: 'papel',
                    header: 'Papel',
                    cell: (inv: Invitation) => <Badge variant="neutral">{inv.roleLabel}</Badge>,
                  },
                  {
                    id: 'convidado-por',
                    header: 'Convidado por',
                    cell: (inv: Invitation) => <div className="!text-[var(--text-muted)]">{inv.invitedBy?.nome || '—'}</div>,
                  },
                  {
                    id: 'situacao',
                    header: 'Situação',
                    cell: (inv: Invitation) => (
                      inv.status === 'EXPIRED'
                        ? <Badge variant="red">Expirado</Badge>
                        : <div className="!text-[var(--text-muted)] text-xs">{`Expira em ${new Date(inv.expiresAt).toLocaleDateString('pt-BR')}`}</div>
                    ),
                  },
                  ...(can('members:manage')
                    ? [{
                      id: 'acoes',
                      header: 'Ações',
                      cell: (inv: Invitation) => (
                        <div className="flex gap-1 items-center">
                          <Button variant="ghost" shape="square" icon={ICONS.Refresh} aria-label={`Reenviar convite para ${inv.email}`} title={'Reenviar (gera um novo link)'} onClick={() => handleResend(inv)} />
                          <Button variant="ghost" shape="square" icon={ICONS.X} aria-label={`Cancelar convite de ${inv.email}`} title={'Cancelar convite'} onClick={() => setPendingRevoke(inv)} />
                        </div>
                      ),
                    }]
                    : []),
                ]}
                ariaLabels={{ tableLabel: 'Convites pendentes', selectionGroupLabel: 'Convites pendentes' }}
              />
            </div>
          </section>
        )}
      </div>

      <DangerConfirmModal
        open={pendingDeleteId !== null}
        title="Remover da organização"
        confirmText="Remover"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      >
        A pessoa perde o acesso a {orgName ? <strong>{orgName}</strong> : 'esta organização'}. A conta continua existindo e o acesso às outras organizações dela não muda.
      </DangerConfirmModal>

      <DangerConfirmModal
        open={pendingRevoke !== null}
        title="Cancelar convite"
        confirmText="Cancelar convite"
        objectName={pendingRevoke?.email}
        onConfirm={confirmRevoke}
        onCancel={() => setPendingRevoke(null)}
      >
        O link enviado para <strong>{pendingRevoke?.email}</strong> deixa de funcionar.
      </DangerConfirmModal>

      <InviteMemberModal
        open={inviteOpen}
        organizationName={orgName}
        canInviteOwners={can('owners:manage')}
        result={inviteResult}
        onSubmit={handleInvite}
        onClose={() => { setInviteOpen(false); setInviteResult(null); }}
      />
    </>
  );
}
